import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import {
  Box,
  Text,
  Button,
  Flex,
  HStack,
  VStack,
  Stack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  FiPackage,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiShoppingBag,
  FiRefreshCw,
  FiStar,
  FiMapPin,
  FiCopy,
  FiChevronRight,
  FiTruck,
  FiList,
  FiExternalLink,
  FiAlertCircle,
  FiCreditCard,
} from "react-icons/fi";
import WithSubnavigation from "../Pages/navbar";
import {
  colors,
  gradients,
  radii,
  shadows,
} from "./designSystem";
import {
  AnimStyles,
  EmptyState,
  SectionHeader,
  TrustStrip,
} from "./Primitives";

import { API_BASE } from "../../config";
import { performLogout } from "../../utils/logout";

const STATUS_META = {
  Confirmed: {
    label: "Confirmed",
    color: colors.info[700],
    bg: colors.info[50],
    border: colors.info[100],
    icon: FiCheckCircle,
  },
  "Invoice Generated": {
    label: "Processing",
    color: colors.warning[700],
    bg: colors.warning[50],
    border: colors.warning[100],
    icon: FiClock,
  },
  Processing: {
    label: "Processing",
    color: colors.warning[700],
    bg: colors.warning[50],
    border: colors.warning[100],
    icon: FiClock,
  },
  "In-Transit": {
    label: "Dispatched",
    color: colors.warning[700],
    bg: colors.warning[50],
    border: colors.warning[100],
    icon: FiTruck,
  },
  Delivered: {
    label: "Delivered",
    color: colors.success[700],
    bg: colors.success[50],
    border: colors.success[100],
    icon: FiCheckCircle,
  },
  Rejected: {
    label: "Rejected",
    color: colors.error[700],
    bg: colors.error[50],
    border: colors.error[100],
    icon: FiXCircle,
  },
  Cancelled: {
    label: "Cancelled",
    color: colors.error[700],
    bg: colors.error[50],
    border: colors.error[100],
    icon: FiXCircle,
  },
  "Under Review": {
    label: "Under Review",
    color: "#065f46",
    bg: "#d1fae5",
    border: "#6ee7b7",
    icon: FiClock,
  },
  "Review needed": {
    label: "Review needed",
    color: "#92400e",
    bg: "#fef9c3",
    border: "#fde68a",
    icon: FiAlertCircle,
  },
  "Payment due": {
    label: "Payment due",
    color: "#1e40af",
    bg: "#eff6ff",
    border: "#bfdbfe",
    icon: FiCreditCard,
  },
};

const TIMELINE_STAGES = [
  { key: "placed", label: "Placed", icon: FiShoppingBag },
  { key: "confirmed", label: "Confirmed", icon: FiCheckCircle },
  { key: "dispatched", label: "Dispatched", icon: FiTruck },
  { key: "delivered", label: "Delivered", icon: FiPackage },
];

const stageIndexForStatus = (status) => {
  if (status === "Delivered") return 3;
  if (status === "In-Transit") return 2;
  if (status === "Invoice Generated") return 1;
  if (status === "Cancelled" || status === "Rejected") return -1;
  if (status === "Confirmed") return 1;
  if (status === "Processing") return 0;
  return 0;
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const StatusPill = ({ status, workflowStage, isPrescriptionOrder }) => {
  let displayKey = status;
  const effectiveStage = workflowStage || (isPrescriptionOrder && status === "Processing" ? "review" : null);
  if (effectiveStage === "rejected") displayKey = "Rejected";
  else if (effectiveStage === "cancelled") displayKey = "Cancelled";
  else if (effectiveStage === "review") displayKey = "Under Review";
  else if (effectiveStage === "pending_user_review") displayKey = "Review needed";
  else if (effectiveStage === "pending_payment" || effectiveStage === "payment_pending") displayKey = "Payment due";
  const meta = STATUS_META[displayKey] || STATUS_META.Processing;
  return (
    <HStack
      spacing={1.5}
      bg={meta.bg}
      color={meta.color}
      border={`1px solid ${meta.border}`}
      borderRadius="full"
      px={3}
      py="4px"
      fontSize="12px"
      fontWeight="800"
      display="inline-flex"
    >
      <Icon as={meta.icon} boxSize={3.5} />
      <Text m={0}>{meta.label}</Text>
    </HStack>
  );
};

const StatCard = ({ label, value, icon: IconComp, tone, active, onClick }) => {
  const palette = {
    brand: { bg: colors.brand[50], fg: colors.brand[700], border: colors.brand[100], activeBorder: colors.brand[400] },
    warning: { bg: colors.warning[50], fg: colors.warning[700], border: colors.warning[100], activeBorder: colors.warning[500] },
    success: { bg: colors.success[50], fg: colors.success[700], border: colors.success[100], activeBorder: colors.success[500] },
    error: { bg: colors.error[50], fg: colors.error[700], border: colors.error[100], activeBorder: colors.error[500] },
  }[tone] || { bg: colors.neutral[50], fg: colors.neutral[700], border: colors.neutral[200], activeBorder: colors.neutral[400] };

  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      flex="1"
      minW={{ base: "140px", md: "180px" }}
      textAlign="left"
      bg="white"
      border={`1.5px solid ${active ? palette.activeBorder : colors.neutral[200]}`}
      borderRadius={radii.lg}
      p={4}
      transition="all 0.2s ease"
      boxShadow={active ? shadows.md : shadows.xs}
      _hover={{ transform: "translateY(-2px)", boxShadow: shadows.md, borderColor: palette.activeBorder }}
      cursor="pointer"
    >
      <HStack spacing={3} align="flex-start">
        <Box
          w="40px"
          h="40px"
          borderRadius="12px"
          bg={palette.bg}
          color={palette.fg}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <Icon as={IconComp} boxSize={5} />
        </Box>
        <Box flex="1" minW={0}>
          <Text fontSize="11px" fontWeight="800" color={colors.neutral[500]} textTransform="uppercase" letterSpacing="0.08em" m={0}>
            {label}
          </Text>
          <Text fontSize="26px" fontWeight="900" color={colors.neutral[900]} m={0} lineHeight="1.1" mt={1}>
            {value}
          </Text>
        </Box>
      </HStack>
    </Box>
  );
};

const Timeline = ({ status, workflowStage }) => {
  const currentIdx = stageIndexForStatus(status);
  const cancelled = status === "Cancelled";
  const rejected = workflowStage === "rejected";

  if (rejected) {
    return (
      <Box
        bg={colors.error[50]}
        border={`1px solid ${colors.error[100]}`}
        borderRadius={radii.md}
        p={3}
        display="flex"
        alignItems="center"
        gap={2}
      >
        <Icon as={FiXCircle} color={colors.error[600]} />
        <Text fontSize="13px" fontWeight="700" color={colors.error[700]} m={0}>
          This order was rejected by the pharmacy. Any charges will be refunded within 3–5 business days.
        </Text>
      </Box>
    );
  }

  if (cancelled) {
    return (
      <Box
        bg={colors.error[50]}
        border={`1px solid ${colors.error[100]}`}
        borderRadius={radii.md}
        p={3}
        display="flex"
        alignItems="center"
        gap={2}
      >
        <Icon as={FiXCircle} color={colors.error[600]} />
        <Text fontSize="13px" fontWeight="700" color={colors.error[700]} m={0}>
          This order was cancelled. Any charges will be refunded within 3–5 business days.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Flex position="relative" justify="space-between" align="center">
        <Box
          position="absolute"
          top="18px"
          left="18px"
          right="18px"
          h="3px"
          bg={colors.neutral[200]}
          borderRadius="full"
          zIndex={0}
        />
        <Box
          position="absolute"
          top="18px"
          left="18px"
          w={`calc((100% - 36px) * ${Math.max(0, currentIdx) / (TIMELINE_STAGES.length - 1)})`}
          h="3px"
          bgGradient={gradients.brand}
          borderRadius="full"
          zIndex={1}
          transition="width 0.5s ease"
        />
        {TIMELINE_STAGES.map((stage, idx) => {
          const done = idx <= currentIdx;
          const active = idx === currentIdx;
          return (
            <VStack key={stage.key} spacing={1.5} zIndex={2} flex="1">
              <Box
                w="36px"
                h="36px"
                borderRadius="full"
                bg={done ? colors.brand[500] : "white"}
                color={done ? "white" : colors.neutral[400]}
                border={`2px solid ${done ? colors.brand[500] : colors.neutral[300]}`}
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxShadow={active ? shadows.brand : "none"}
                style={active ? { animation: "pillstack-pulse 1.6s ease-in-out infinite" } : {}}
              >
                <Icon as={stage.icon} boxSize={4} />
              </Box>
              <Text
                fontSize="11px"
                fontWeight={done ? "800" : "600"}
                color={done ? colors.neutral[900] : colors.neutral[500]}
                m={0}
                textAlign="center"
              >
                {stage.label}
              </Text>
            </VStack>
          );
        })}
      </Flex>
    </Box>
  );
};

const OrderLineItem = ({ item, originalPrice, discountPercent, isUnavailable }) => {
  const finalPrice = Number(item?.price);
  const hasPrice = Number.isFinite(finalPrice) && finalPrice > 0;
  const hasOriginal = originalPrice > 0 && Math.abs(originalPrice - finalPrice) > 0.01;
  const hasDiscount = discountPercent > 0;

  return (
    <Flex align="flex-start" justify="space-between" gap={3} py={2}>
      <HStack spacing={2} minW={0} flex={1} align="flex-start">
        <Box
          w="6px" h="6px" borderRadius="full" mt="5px" flexShrink={0}
          bg={isUnavailable ? "#dc2626" : colors.brand[400]}
        />
        <Box minW={0}>
          <HStack spacing={2} flexWrap="wrap">
            <Text
              fontSize="13px" m={0} noOfLines={1}
              color={isUnavailable ? colors.neutral[400] : colors.neutral[800]}
              textDecoration={isUnavailable ? "line-through" : "none"}
            >
              {item?.name || item?.product_name || "Medicine"}
            </Text>
            {isUnavailable && (
              <Box as="span" fontSize="10px" fontWeight="700" color="#dc2626" bg="#fff1f2" px={2} py="1px" borderRadius="full" flexShrink={0}>
                Unavailable
              </Box>
            )}
          </HStack>
          {!isUnavailable && (hasOriginal || hasDiscount) && (
            <HStack spacing={1.5} mt={0.5} flexWrap="wrap">
              {hasOriginal && (
                <Text fontSize="11px" color="#94a3b8" textDecoration="line-through" m={0}>
                  ₹{Number(originalPrice).toFixed(2)}
                </Text>
              )}
              {hasDiscount && (
                <Box as="span" fontSize="10px" fontWeight="700" color="#059669" bg="#d1fae5" px={1.5} py="1px" borderRadius="full">
                  -{discountPercent}% off
                </Box>
              )}
            </HStack>
          )}
        </Box>
      </HStack>
      <Box textAlign="right" flexShrink={0}>
        <Text fontSize="12px" color={colors.neutral[500]} m={0}>×{item?.quantity || 1}</Text>
        {isUnavailable ? (
          <Text fontSize="12px" color="#94a3b8" textDecoration="line-through" m={0}>
            ₹{Number(originalPrice || item?.price || 0).toFixed(2)}
          </Text>
        ) : (
          <Text fontSize="13px" fontWeight="700" color={hasPrice ? colors.brand[700] : colors.neutral[400]} m={0}>
            {hasPrice ? `₹${finalPrice.toFixed(2)}` : "—"}
          </Text>
        )}
      </Box>
    </Flex>
  );
};

const StarRating = ({ value, onChange }) => (
  <HStack spacing={1}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Box
        key={n}
        as="button"
        type="button"
        onClick={() => onChange(n)}
        color={n <= value ? colors.warning[500] : colors.neutral[300]}
        _hover={{ transform: "scale(1.15)" }}
        transition="all 0.15s ease"
      >
        <Icon as={FiStar} boxSize={7} fill={n <= value ? colors.warning[500] : "none"} />
      </Box>
    ))}
  </HStack>
);

const OrderDetailsPage = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [chatLink, setChatLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const [rateOrder, setRateOrder] = useState(null);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");
  const [ratedIds, setRatedIds] = useState(
    () => new Set(JSON.parse(typeof window !== "undefined" ? localStorage.getItem("pillstackRatedOrders") || "[]" : "[]")),
  );
  const { isOpen: isRateOpen, onOpen: onRateOpen, onClose: onRateClose } = useDisclosure();
  const navigate = useNavigate();
  const toast = useToast();
  const [, , removeCookie] = useCookies(["token", "role"]);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const { data } = await axios.post(`${API_BASE}`, {}, { withCredentials: true });
        if (data?.status && data?.user) {
          setUsername(data.user);
          const full = await axios.get(`${API_BASE}/patient/myInfo/${data.user}`);
          setFullName(full.data?.fullName || full.data?.Name || "");
          try {
            const doc = await axios.get(`${API_BASE}/patient/getDoctorUsername/${data.user}`);
            if (doc?.data?.doctorUsername) setChatLink(`/chatwithdoctor/${doc.data.doctorUsername}`);
          } catch {}
          try {
            const cartResp = await axios.get(`${API_BASE}/cart`, { withCredentials: true });
            setCartCount(Array.isArray(cartResp?.data?.items) ? cartResp.data.items.length : 0);
          } catch {}
        }
      } catch {}
    };
    loadUser();
  }, []);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE}/patient/orderDetails`, {
          withCredentials: true,
        });
        setOrders(response.data || []);
      } catch (error) {
        console.error("Error fetching order details:", error);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    fetchOrderDetails();
  }, []);

  const safeOrders = Array.isArray(orders) ? orders : [];

  const filteredOrders = useMemo(() => {
    if (filter === "processing") return safeOrders.filter((o) => o.Status === "Processing" || o.Status === "Confirmed" || o.Status === "In-Transit" || o.Status === "Invoice Generated");
    if (filter === "delivered") return safeOrders.filter((o) => o.Status === "Delivered");
    if (filter === "cancelled") return safeOrders.filter((o) => o.Status === "Cancelled" || o.Status === "Rejected");
    return safeOrders;
  }, [safeOrders, filter]);

  const stats = useMemo(() => {
    const delivered = safeOrders.filter((o) => o.Status === "Delivered").length;
    const processing = safeOrders.filter((o) => o.Status === "Processing" || o.Status === "Confirmed" || o.Status === "In-Transit" || o.Status === "Invoice Generated").length;
    const cancelled = safeOrders.filter((o) => o.Status === "Cancelled" || o.Status === "Rejected").length;
    return { total: safeOrders.length, delivered, processing, cancelled };
  }, [safeOrders]);

  const handleLogout = () => performLogout(removeCookie);

  const handleCancelOrder = async (orderId) => {
    try {
      const response = await axios.post(
        `${API_BASE}/order/cancel-order`,
        { orderId },
        { withCredentials: true },
      );
      if (response.status === 200 && response.data === "Order cancelled successfully") {
        setOrders((prev) => prev.map((o) => (o._id === orderId ? { ...o, Status: "Cancelled" } : o)));
        toast({ title: "Order cancelled", status: "success", position: "top-right", duration: 3000 });
      } else {
        toast({ title: "Could not cancel", status: "error", position: "top-right", duration: 3000 });
      }
    } catch (error) {
      toast({ title: "Could not cancel", status: "error", position: "top-right", duration: 3000 });
    }
  };

  const handleReorder = async (order) => {
    const items = order?.Items || order?.items || [];
    if (!items.length) return;
    let added = 0;
    for (const it of items) {
      try {
        await axios.post(
          `${API_BASE}/cart/add`,
          {
            medicineName: it.name,
            quantity: it.quantity || 1,
            price: it.price,
          },
          { withCredentials: true },
        );
        added += 1;
      } catch {}
    }
    toast({
      title: added ? `Added ${added} item${added > 1 ? "s" : ""} to cart` : "Couldn't reorder",
      status: added ? "success" : "error",
      position: "top-right",
      duration: 2500,
    });
    if (added) navigate("/cart");
  };

  const openRateDialog = (order) => {
    setRateOrder(order);
    setRating(0);
    setReview("");
    onRateOpen();
  };

  const submitRating = () => {
    if (!rating || !rateOrder) return;
    const next = new Set(ratedIds);
    next.add(rateOrder._id);
    setRatedIds(next);
    try {
      localStorage.setItem("pillstackRatedOrders", JSON.stringify(Array.from(next)));
    } catch {}
    onRateClose();
    toast({
      title: "Thanks for rating!",
      description: "Your feedback helps other patients.",
      status: "success",
      position: "top-right",
      duration: 3000,
    });
  };

  const copyVoucher = (id) => {
    try {
      navigator.clipboard.writeText(id);
      toast({
        title: "Copied!",
        description: "Order reference copied to clipboard.",
        status: "success",
        position: "top-right",
        duration: 1800,
      });
    } catch {}
  };

  const renderOrderCard = (order) => {
    const items = order.Items || order.items || [];
    const placedOn = order.dateAdded || order.date_added;
    const isDelivered = order.Status === "Delivered";
    const isProcessing = order.Status === "Processing";
    const rated = ratedIds.has(order._id);

    const effectiveStage = order.workflowStage || (order.isPrescriptionOrder && order.Status === "Processing" ? "review" : null);
    const isUnderReview = effectiveStage === "review";
    const isPendingReview = effectiveStage === "pending_user_review";
    const isPendingPayment = effectiveStage === "pending_payment" || effectiveStage === "payment_pending";
    const isPendingAny = isUnderReview || isPendingReview || isPendingPayment;

    // Calculate total from pharmacist fulfillment if available
    const fulfillmentLines = order.pharmacistFulfillment?.lines || [];
    const deliveryFee = Number(
      order.deliveryWorkflow?.selectedOption?.deliveryFee ??
      order.deliveryWorkflow?.quote?.deliveryFee ??
      0
    );
    let medicinesTotal = Number(order.bill || 0);
    if (fulfillmentLines.length > 0) {
      medicinesTotal = fulfillmentLines.reduce((total, line) => {
        if (line.isOutOfStock) return total;
        const allocation = line.allocations?.[0];
        const saleRate = allocation?.saleRate || line.mrpOverride || 0;
        const discountPercent = line.discountPercent || 0;
        const qty = line.requestedQty || line.quantity || 1;
        const unitPrice = saleRate > 0 
          ? saleRate 
          : (line.mrpOverride || 0) * (1 - discountPercent / 100);
        return total + (unitPrice * qty);
      }, 0);
    }
    const orderTotal = medicinesTotal + deliveryFee;

    return (
      <Box
        key={order._id}
        bg="white"
        borderRadius={radii.lg}
        border={`1px solid ${colors.neutral[200]}`}
        boxShadow={shadows.sm}
        overflow="hidden"
        _hover={{ boxShadow: shadows.md, borderColor: colors.brand[200] }}
        transition="all 0.2s ease"
      >
        {/* Header */}
        <Flex
          direction={{ base: "column", md: "row" }}
          align={{ base: "flex-start", md: "center" }}
          justify="space-between"
          gap={3}
          px={{ base: 4, md: 5 }}
          py={4}
          bg={colors.neutral[50]}
          borderBottom={`1px solid ${colors.neutral[200]}`}
        >
          <HStack spacing={3} minW={0}>
            <Box
              w="44px"
              h="44px"
              borderRadius={radii.md}
              bgGradient={gradients.brandSoft}
              color={colors.brand[700]}
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Icon as={FiPackage} boxSize={5} />
            </Box>
            <Box minW={0}>
              <Text fontSize="13px" fontWeight="800" color={colors.neutral[900]} m={0}>
                Order #{order._id.slice(-8)}
              </Text>
              <HStack spacing={2} mt={0.5}>
                <Text fontSize="12px" color={colors.neutral[500]} m={0}>
                  {formatDate(placedOn)}
                </Text>
                <Text fontSize="12px" color={colors.neutral[400]} m={0}>·</Text>
                <Text fontSize="12px" color={colors.neutral[600]} fontWeight="700" m={0}>
                  {items.length} item{items.length !== 1 ? "s" : ""}
                </Text>
              </HStack>
            </Box>
          </HStack>
          <HStack spacing={3}>
            <StatusPill status={order.Status} workflowStage={order.workflowStage} isPrescriptionOrder={order.isPrescriptionOrder} />
            <Box textAlign="right">
              <Text fontSize="10px" color={colors.neutral[500]} textTransform="uppercase" letterSpacing="0.08em" fontWeight="800" m={0}>
                {isPendingAny ? "Est. total" : "Paid"}
              </Text>
              <Text fontSize="18px" fontWeight="900" color={colors.brand[700]} m={0} lineHeight="1.1">
                ₹{orderTotal.toFixed(2)}
              </Text>
            </Box>
          </HStack>
        </Flex>

        {/* Body */}
        <Box px={{ base: 4, md: 5 }} py={5}>
          {/* Timeline */}
          <Box mb={5}>
            <Timeline status={order.Status} workflowStage={order.workflowStage} />
          </Box>

          {/* Items */}
          {(() => {
            const fulfillmentLines = order.pharmacistFulfillment?.lines || [];
            const hasFulfillment = fulfillmentLines.length > 0;
            
            // If pharmacist fulfillment exists, use it as primary source
            if (hasFulfillment) {
              const fulfilledItems = fulfillmentLines.filter(line => !line.isOutOfStock);
              const outOfStockItems = fulfillmentLines.filter(line => line.isOutOfStock);
              
              return (
                <Box>
                  <Text fontSize="11px" fontWeight="800" color={colors.neutral[500]} textTransform="uppercase" letterSpacing="0.08em" m={0} mb={2}>
                    Medicines
                  </Text>
                  <Stack spacing={0} divider={<Box borderBottom={`1px solid ${colors.neutral[100]}`} />}>
                    {fulfilledItems.map((line, idx) => {
                      // Calculate final price from allocations (saleRate is the confirmed price)
                      const allocation = line.allocations?.[0];
                      const saleRate = allocation?.saleRate || line.mrpOverride || 0;
                      const discountPercent = line.discountPercent || 0;
                      const finalUnitPrice = saleRate > 0 
                        ? saleRate 
                        : (line.mrpOverride || 0) * (1 - discountPercent / 100);
                      const qty = line.requestedQty || line.quantity || 1;
                      const totalPrice = finalUnitPrice * qty;
                      
                      return (
                        <OrderLineItem
                          key={`${order._id}-fl-${idx}`}
                          item={{
                            name: line.itemName,
                            quantity: qty,
                            price: finalUnitPrice,
                            composition: line.itemName,
                          }}
                          originalPrice={line.mrpOverride}
                          discountPercent={discountPercent}
                        />
                      );
                    })}
                    {outOfStockItems.map((line, idx) => {
                      const srcItem = items.find(it => it.name === line.itemName);
                      return (
                        <OrderLineItem
                          key={`${order._id}-unavail-${idx}`}
                          item={{
                            name: line.itemName,
                            quantity: line.requestedQty || 1,
                            price: line.mrpOverride || srcItem?.price || 0,
                            composition: srcItem?.composition || srcItem?.key_ingredients,
                          }}
                          originalPrice={line.mrpOverride || srcItem?.price}
                          isUnavailable
                        />
                      );
                    })}
                    {fulfilledItems.length === 0 && outOfStockItems.length === 0 && (
                      <Text fontSize="13px" color={colors.neutral[500]} m={0}>No item details available.</Text>
                    )}
                  </Stack>
                </Box>
              );
            }
            
            // Fallback to original items when no pharmacist fulfillment
            const changesMap = {};
            (order.proposedChanges || []).forEach(c => {
              changesMap[c.requestedItemName] = c;
            });
            const unavailableItems = (order.proposedChanges || []).filter(c => c.changeType === 'unavailable');
            const unavailableNames = new Set(unavailableItems.map(c => c.requestedItemName));
            const availableItems = items.filter(it => !unavailableNames.has(it.name));
            return (
              <Box>
                <Text fontSize="11px" fontWeight="800" color={colors.neutral[500]} textTransform="uppercase" letterSpacing="0.08em" m={0} mb={2}>
                  Medicines
                </Text>
                <Stack spacing={0} divider={<Box borderBottom={`1px solid ${colors.neutral[100]}`} />}>
                  {availableItems.map((it, idx) => {
                    const ch = changesMap[it.name];
                    const originalPrice = ch?.changeType === 'price_change' ? ch.requestedPrice : null;
                    return (
                      <OrderLineItem
                        key={`${order._id}-${idx}`}
                        item={it}
                        originalPrice={originalPrice}
                        discountPercent={0}
                      />
                    );
                  })}
                  {unavailableItems.map((c, idx) => {
                    const srcItem = items.find(it => it.name === c.requestedItemName);
                    return (
                      <OrderLineItem
                        key={`${order._id}-unavail-${idx}`}
                        item={{
                          name: c.requestedItemName,
                          quantity: c.requestedQuantity,
                          price: c.requestedPrice,
                          composition: srcItem?.composition || srcItem?.key_ingredients,
                        }}
                        originalPrice={c.requestedPrice}
                        isUnavailable
                      />
                    );
                  })}
                  {availableItems.length === 0 && unavailableItems.length === 0 && (
                    <Text fontSize="13px" color={colors.neutral[500]} m={0}>No item details available.</Text>
                  )}
                </Stack>
              </Box>
            );
          })()}

          {/* Bill breakdown */}
          {(() => {
            const deliveryFee = Number(
              order.deliveryWorkflow?.selectedOption?.deliveryFee ??
              order.deliveryWorkflow?.quote?.deliveryFee ??
              0
            );
            // Calculate medicines total from pharmacist fulfillment if available
            const fulfillmentLines = order.pharmacistFulfillment?.lines || [];
            let medicines = Number(order.bill || 0);
            if (fulfillmentLines.length > 0) {
              medicines = fulfillmentLines.reduce((total, line) => {
                if (line.isOutOfStock) return total;
                const mrp = Number(line.mrpOverride || 0);
                const saleRate = line.allocations?.[0]?.saleRate || 0;
                const discountPercent = Number(line.discountPercent || 0);
                const qty = Number(line.requestedQty || line.quantity || 1);
                const unitPrice = mrp > 0
                  ? mrp * (1 - discountPercent / 100)
                  : (saleRate > 0 ? saleRate : 0);
                return total + (unitPrice * qty);
              }, 0);
              const grossDiscountPct = Number(order.pharmacistFulfillment?.grossDiscountPercent || 0);
              if (grossDiscountPct > 0) {
                medicines = medicines * (1 - grossDiscountPct / 100);
              }
            }
            const total = medicines + deliveryFee;
            return (
              <Box mt={4} bg={colors.neutral[50]} border={`1px solid ${colors.neutral[200]}`} borderRadius={radii.md} px={3} py={3}>
                <Stack spacing={1.5}>
                  <Flex justify="space-between" align="center">
                    <Text fontSize="12px" color={colors.neutral[500]} m={0}>Medicines</Text>
                    <Text fontSize="12px" fontWeight="700" color={colors.neutral[800]} m={0}>₹{medicines.toFixed(2)}</Text>
                  </Flex>
                  <Flex justify="space-between" align="center">
                    <Text fontSize="12px" color={colors.neutral[500]} m={0}>Delivery</Text>
                    <Text fontSize="12px" fontWeight="700" color={colors.neutral[800]} m={0}>
                      {deliveryFee > 0 ? `₹${deliveryFee.toFixed(2)}` : "—"}
                    </Text>
                  </Flex>
                  <Box h="1px" bg={colors.neutral[200]} />
                  <Flex justify="space-between" align="center">
                    <Text fontSize="12px" fontWeight="800" color={colors.neutral[900]} m={0}>Total</Text>
                    <Text fontSize="14px" fontWeight="900" color={colors.brand[700]} m={0}>₹{total.toFixed(2)}</Text>
                  </Flex>
                </Stack>
              </Box>
            );
          })()}

          {/* Address + voucher */}
          <Flex
            mt={5}
            direction={{ base: "column", md: "row" }}
            gap={3}
            align="stretch"
          >
            <Box
              flex="1"
              bg={colors.neutral[50]}
              border={`1px solid ${colors.neutral[200]}`}
              borderRadius={radii.md}
              p={3}
            >
              <HStack spacing={2} color={colors.neutral[500]}>
                <Icon as={FiMapPin} boxSize={3.5} />
                <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.08em" m={0}>
                  Delivery address
                </Text>
              </HStack>
              <Text fontSize="13px" fontWeight="700" color={colors.neutral[800]} m={0} mt={1} noOfLines={2}>
                {order.address || "Not provided"}
              </Text>
            </Box>
            <Box
              flex="1"
              bg={colors.neutral[50]}
              border={`1px solid ${colors.neutral[200]}`}
              borderRadius={radii.md}
              p={3}
            >
              <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.08em" color={colors.neutral[500]} m={0}>
                Voucher
              </Text>
              <HStack spacing={2} mt={1}>
                <Text fontSize="12px" fontFamily="mono" color={colors.neutral[800]} m={0} noOfLines={1}>
                  {order._id}
                </Text>
                <Box
                  as="button"
                  type="button"
                  onClick={() => copyVoucher(order._id)}
                  color={colors.brand[600]}
                  _hover={{ color: colors.brand[700] }}
                  aria-label="Copy voucher"
                >
                  <Icon as={FiCopy} boxSize={3.5} />
                </Box>
              </HStack>
            </Box>
          </Flex>

          {/* Delivery tracking banner */}
          {(order.deliveryWorkflow?.borzoStatus || order.deliveryWorkflow?.status) && order.deliveryWorkflow?.borzoOrderId && (
            <Flex
              mt={4}
              p={3}
              bg={colors.brand[50]}
              border={`1px solid ${colors.brand[100]}`}
              borderRadius={radii.md}
              align="center"
              justify="space-between"
              gap={3}
              wrap="wrap"
            >
              <HStack spacing={2} color={colors.brand[700]}>
                <Icon as={FiTruck} boxSize={4} />
                <VStack align="flex-start" spacing={0}>
                  <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.08em" m={0}>
                    Delivery status
                  </Text>
                  <Text fontSize="13px" fontWeight="700" color={colors.neutral[800]} m={0}>
                    {(order.deliveryWorkflow.borzoStatus || order.deliveryWorkflow.status || '')
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </Text>
                  {order.deliveryWorkflow.courierName && (
                    <Text fontSize="11px" color={colors.neutral[600]} m={0}>
                      Courier: {order.deliveryWorkflow.courierName}
                      {order.deliveryWorkflow.courierPhone ? ` · ${order.deliveryWorkflow.courierPhone}` : ''}
                    </Text>
                  )}
                </VStack>
              </HStack>
            </Flex>
          )}

          {/* Review / Payment banners */}
          {(order.workflowStage === "review" || (!order.workflowStage && order.isPrescriptionOrder && order.Status === "Processing")) && (
            <Flex
              mt={4}
              p={3}
              bg="#d1fae5"
              border="1px solid #6ee7b7"
              borderRadius={radii.md}
              align="flex-start"
              gap={3}
            >
              <Icon as={FiClock} color="#065f46" boxSize={4} mt="2px" flexShrink={0} />
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="13px" fontWeight="800" color="#065f46" m={0}>
                  Order sent to pharmacy
                </Text>
                <Text fontSize="12px" color="#047857" m={0}>
                  The pharmacist is reviewing your order. You'll be notified when it's confirmed or if changes are needed.
                </Text>
              </VStack>
            </Flex>
          )}
          {order.workflowStage === "pending_user_review" && (
            <Flex
              mt={4}
              p={3}
              bg="#fef9c3"
              border="1px solid #fde68a"
              borderRadius={radii.md}
              align="flex-start"
              gap={3}
            >
              <Icon as={FiAlertCircle} color="#92400e" boxSize={4} mt="2px" flexShrink={0} />
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="13px" fontWeight="800" color="#92400e" m={0}>
                  Pharmacist has proposed changes
                </Text>
                <Text fontSize="12px" color="#78350f" m={0}>
                  Some items may be unavailable or have updated pricing. Review and accept to proceed to payment.
                </Text>
              </VStack>
            </Flex>
          )}
          {(order.workflowStage === "pending_payment" || order.workflowStage === "payment_pending") && (
            <Flex
              mt={4}
              p={3}
              bg="#eff6ff"
              border="1px solid #bfdbfe"
              borderRadius={radii.md}
              align="flex-start"
              gap={3}
            >
              <Icon as={FiCreditCard} color="#1e40af" boxSize={4} mt="2px" flexShrink={0} />
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="13px" fontWeight="800" color="#1e40af" m={0}>
                  Ready for payment
                </Text>
                <Text fontSize="12px" color="#1e3a8a" m={0}>
                  The pharmacist has confirmed your order. Complete payment to confirm your order.
                </Text>
              </VStack>
            </Flex>
          )}

          {/* Actions */}
          <Flex
            mt={5}
            pt={4}
            borderTop={`1px dashed ${colors.neutral[200]}`}
            direction={{ base: "column", sm: "row" }}
            gap={3}
            align={{ base: "stretch", sm: "center" }}
            justify="flex-end"
            wrap="wrap"
          >
            {isProcessing && !isPendingAny ? (
              <Button
                variant="outline"
                borderColor={colors.error[100]}
                color={colors.error[600]}
                size="sm"
                leftIcon={<Icon as={FiXCircle} />}
                _hover={{ bg: colors.error[50], borderColor: colors.error[500] }}
                onClick={() => handleCancelOrder(order._id)}
              >
                Cancel order
              </Button>
            ) : null}

            {isDelivered ? (
              <>
                {!rated ? (
                  <Button
                    variant="outline"
                    borderColor={colors.warning[100]}
                    color={colors.warning[700]}
                    size="sm"
                    leftIcon={<Icon as={FiStar} />}
                    _hover={{ bg: colors.warning[50], borderColor: colors.warning[500] }}
                    onClick={() => openRateDialog(order)}
                  >
                    Rate experience
                  </Button>
                ) : (
                  <HStack
                    spacing={1}
                    px={3}
                    py="6px"
                    bg={colors.success[50]}
                    border={`1px solid ${colors.success[100]}`}
                    borderRadius="full"
                  >
                    <Icon as={FiCheckCircle} color={colors.success[700]} boxSize={3.5} />
                    <Text fontSize="12px" fontWeight="800" color={colors.success[700]} m={0}>
                      Thanks for rating
                    </Text>
                  </HStack>
                )}
                <Button
                  bgGradient={gradients.brand}
                  color="white"
                  size="sm"
                  leftIcon={<Icon as={FiRefreshCw} />}
                  _hover={{ bgGradient: gradients.brandBold, boxShadow: shadows.brandHover, transform: "translateY(-1px)" }}
                  onClick={() => handleReorder(order)}
                >
                  Reorder
                </Button>
              </>
            ) : null}

            {order.deliveryWorkflow?.trackingUrl ? (
              <Button
                as="a"
                href={order.deliveryWorkflow.trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outline"
                colorScheme="teal"
                size="sm"
                leftIcon={<Icon as={FiTruck} />}
                rightIcon={<Icon as={FiExternalLink} />}
              >
                Track live
              </Button>
            ) : null}

            {isUnderReview ? (
              <Button
                size="sm"
                bg="#059669"
                color="white"
                fontWeight="700"
                leftIcon={<Icon as={FiClock} />}
                _hover={{ bg: "#047857" }}
                onClick={() => navigate(`/order/prescription/${order._id}`, { state: { order } })}
              >
                View order
              </Button>
            ) : null}

            {isPendingReview ? (
              <Button
                size="sm"
                bg="#d97706"
                color="white"
                fontWeight="700"
                leftIcon={<Icon as={FiAlertCircle} />}
                _hover={{ bg: "#b45309" }}
                onClick={() => navigate(`/order/prescription/${order._id}`, { state: { order } })}
              >
                Review changes
              </Button>
            ) : null}

            {isPendingPayment ? (
              <Button
                size="sm"
                bg="#1d4ed8"
                color="white"
                fontWeight="700"
                leftIcon={<Icon as={FiCreditCard} />}
                _hover={{ bg: "#1e40af" }}
                onClick={() => navigate(`/order/prescription/${order._id}`, { state: { order } })}
              >
                Pay now
              </Button>
            ) : null}

            {order.isPrescriptionOrder && order.workflowStage && !isPendingAny ? (
              <Button
                variant="ghost"
                color={colors.brand[700]}
                size="sm"
                rightIcon={<Icon as={FiChevronRight} />}
                _hover={{ bg: colors.brand[50] }}
                onClick={() =>
                  navigate(`/order/prescription/${order._id}`, { state: { order } })
                }
              >
                Track order
              </Button>
            ) : null}
          </Flex>
        </Box>
      </Box>
    );
  };

  return (
    <Box bg={colors.neutral[50]} minH="100vh" overflowX="hidden">
      <AnimStyles />
      <WithSubnavigation
        username={username}
        name={fullName}
        onLogout={handleLogout}
        chatLink={chatLink}
        cartCount={cartCount}
        sidebarOffset="0px"
        isLoggedIn={Boolean(username)}
      />
      <Box>
        <Box px={{ base: 4, md: 8 }} py={{ base: 4, md: 6 }} maxW="900px" mx="auto">
          {/* Header */}
          <SectionHeader
            eyebrow="My Orders"
            title="Your order history"
            description="Track prescriptions, deliveries, and reorder your usual medicines in one tap."
          />

          {/* Interactive stats */}
          <Stack
            direction={{ base: "column", sm: "row" }}
            spacing={3}
            mb={5}
            wrap={{ base: "nowrap", md: "wrap" }}
          >
            <StatCard
              label="Total orders"
              value={stats.total}
              icon={FiList}
              tone="brand"
              active={filter === "all"}
              onClick={() => setFilter("all")}
            />
            <StatCard
              label="Processing"
              value={stats.processing}
              icon={FiClock}
              tone="warning"
              active={filter === "processing"}
              onClick={() => setFilter("processing")}
            />
            <StatCard
              label="Delivered"
              value={stats.delivered}
              icon={FiCheckCircle}
              tone="success"
              active={filter === "delivered"}
              onClick={() => setFilter("delivered")}
            />
            <StatCard
              label="Cancelled"
              value={stats.cancelled}
              icon={FiXCircle}
              tone="error"
              active={filter === "cancelled"}
              onClick={() => setFilter("cancelled")}
            />
          </Stack>

          {/* Active filter hint */}
          {filter !== "all" ? (
            <Flex
              align="center"
              justify="space-between"
              bg={colors.brand[50]}
              border={`1px solid ${colors.brand[100]}`}
              borderRadius={radii.md}
              px={4}
              py={2.5}
              mb={4}
            >
              <HStack spacing={2}>
                <Icon as={FiList} color={colors.brand[600]} />
                <Text fontSize="13px" fontWeight="700" color={colors.brand[800]} m={0}>
                  Showing {filteredOrders.length} {filter} order{filteredOrders.length !== 1 ? "s" : ""}
                </Text>
              </HStack>
              <Button
                size="xs"
                variant="ghost"
                color={colors.brand[700]}
                onClick={() => setFilter("all")}
                _hover={{ bg: colors.brand[100] }}
              >
                Clear filter
              </Button>
            </Flex>
          ) : null}

          {/* Orders list */}
          {loading ? (
            <Stack spacing={4}>
              {[1, 2, 3].map((i) => (
                <Box
                  key={i}
                  bg="white"
                  borderRadius={radii.lg}
                  border={`1px solid ${colors.neutral[200]}`}
                  p={5}
                  h="240px"
                  style={{
                    background: `linear-gradient(90deg, ${colors.neutral[100]} 0%, ${colors.neutral[200]} 50%, ${colors.neutral[100]} 100%)`,
                    backgroundSize: "800px 100%",
                    animation: "pillstack-shimmer 1.4s linear infinite",
                  }}
                />
              ))}
            </Stack>
          ) : filteredOrders.length > 0 ? (
            <Stack spacing={4}>{filteredOrders.map(renderOrderCard)}</Stack>
          ) : (
            <Box>
              <EmptyState
                icon={FiPackage}
                title={filter === "all" ? "No orders yet" : `No ${filter} orders`}
                subtitle={
                  filter === "all"
                    ? "Your confirmed orders will show up here. Upload a prescription or browse the catalog to place your first one."
                    : "Nothing matches this filter right now. Try viewing all orders."
                }
                primaryAction={
                  filter === "all"
                    ? { label: "Browse medicines", onClick: () => navigate("/medicine"), icon: FiShoppingBag }
                    : { label: "Show all orders", onClick: () => setFilter("all"), icon: FiList }
                }
                secondaryAction={
                  filter === "all"
                    ? { label: "Upload prescription", onClick: () => navigate("/home") }
                    : null
                }
              />
              {filter === "all" ? (
                <Box mt={6}>
                  <TrustStrip />
                </Box>
              ) : null}
            </Box>
          )}
        </Box>
      </Box>

      {/* Rate experience modal */}
      <Modal isOpen={isRateOpen} onClose={onRateClose} isCentered size="md">
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />
        <ModalContent borderRadius={radii.xl} overflow="hidden">
          <Box bgGradient={gradients.brand} h="80px" display="flex" alignItems="center" justifyContent="center">
            <Box
              w="56px"
              h="56px"
              borderRadius="full"
              bg="rgba(255,255,255,0.2)"
              color="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FiStar} boxSize={7} />
            </Box>
          </Box>
          <ModalHeader
            fontSize="22px"
            fontWeight="800"
            color={colors.neutral[900]}
            letterSpacing="-0.02em"
            textAlign="center"
            pb={1}
          >
            Rate your experience
          </ModalHeader>
          <ModalCloseButton color="white" top={3} right={3} />
          <ModalBody pb={4}>
            <Text fontSize="14px" color={colors.neutral[500]} textAlign="center" m={0} mb={5}>
              How was your order with PillStack? Your feedback helps other patients.
            </Text>
            <Flex justify="center" mb={5}>
              <StarRating value={rating} onChange={setRating} />
            </Flex>
            <Textarea
              placeholder="Anything you'd like the pharmacist to know? (optional)"
              value={review}
              onChange={(e) => setReview(e.target.value)}
              borderColor={colors.neutral[200]}
              _focus={{ borderColor: colors.brand[400], boxShadow: `0 0 0 1px ${colors.brand[400]}` }}
              rows={3}
              fontSize="14px"
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              color={colors.neutral[500]}
              onClick={onRateClose}
              mr={2}
              _hover={{ color: colors.neutral[900] }}
            >
              Maybe later
            </Button>
            <Button
              bgGradient={gradients.brand}
              color="white"
              isDisabled={!rating}
              onClick={submitRating}
              fontWeight="800"
              _hover={{ bgGradient: gradients.brandBold }}
            >
              Submit rating
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default OrderDetailsPage;
